import "bootstrap"; // Required for the `popover` method
import { Widget } from "@foretagsplatsen/widgetjs";
import BudgetEditorFieldWidget from "../BudgetEditorFieldWidget/BudgetEditorFieldWidget.js";
import BudgetEditorRowTitleRenderer from "../BudgetEditorRowTitleRenderer/BudgetEditorRowTitleRenderer.js";
import jQuery from "jquery";
import ReportTableWidget from "../../../reports2/ReportTableWidget/ReportTableWidget.js";

/**
 * The main budget editor widget.
 *
 * Display the budget editor and handles the keyboard navigation.
 *
 * @param {BudgetReport} budgetReport - The budget report to be edited
 * @param {function} onBudgetChanged - Function called when some
 *   budget items have changed
 * @param {FiscalYear[]} [fiscalYears] - The fiscal years of the company
 *   owning the budget. This is used in the popover displayed when the
 *   user clicks an account number
 * @param {function} onAccountStatementRedirectionRequest - Will be
 *   called with an account number and a period and should open the
 *   account statement for this account number at this period
 */
export default class BudgetEditorWidget extends Widget {
	constructor({
		budgetReport,
		onBudgetChanged,
		fiscalYears = [],
		onAccountStatementRedirectionRequest,
	}) {
		super(...arguments);

		this._budgetReport = budgetReport;
		this._onBudgetChanged = onBudgetChanged;

		this._sortedFiscalYears = [...fiscalYears].sort((year1, year2) =>
			year1.endsBefore(year2) ? 1 : -1,
		);

		this._onAccountStatementRedirectionRequest =
			onAccountStatementRedirectionRequest;

		// Used to store the instances of BudgetEditorFieldWidget
		// being created so we can find them back later:
		this._budgetEditorFieldWidgetCollector = undefined;
	}

	renderContentOn(html) {
		if (!this.getReport()) return;

		html.render(
			html.div(
				{ klass: "budget-editor" },
				this.renderReportOn.bind(this),
			),
		);
	}

	renderReportOn(html) {
		html.render(this._budgetEditorRenderer());
	}

	getReport() {
		return this._budgetReport.getReport();
	}

	_budgetEditorRenderer() {
		return (html) => {
			this._budgetEditorFieldWidgetCollector = new Array();

			const reportTableWidget = new ReportTableWidget({
				report: this.getReport(),
				makeRowTitleRenderer: this._makeRowTitleRenderer.bind(this),
				dataCellRenderer: this._dataCellRenderer.bind(this),
				budgetNameRenderer: (_item) => (_html) => {},
			});

			html.div({ klass: "reportViewer" }, reportTableWidget);
		};
	}

	_makeRowTitleRenderer(spec) {
		const visitor = new BudgetEditorRowTitleRenderer({
			...spec,
			onAccountStatementRedirectionRequest:
				this._onAccountStatementRedirectionRequest,
			periods: this._sortedFiscalYears,
		});

		return (row) => visitor.visit(row);
	}

	_dataCellRenderer({ row, column, content }) {
		return (html) =>
			html.td(
				{ klass: "budget-cell amount" },
				content ? this._budgetEditorFieldRenderer(row, column) : "",
			);
	}

	_isYearToDateColumn(column) {
		return column === this.getReport().getColumns()[0];
	}

	_onBudgetFieldChanged({ value, column, row }) {
		if (this._isYearToDateColumn(column)) {
			// Divide `value` by the number of period columns and
			// spread the value in both the budget and report:
			this._budgetReport.distributeYearlyAmount({ value, row });

			// Write the YTD column value in the report:
			this.getReport().setCellData({ column, row, data: value });

			// Refresh the cell widgets so the user immediately sees
			// the YTD column value being distributed:
			this._updateValuesOfAutomatedCells({ force: true });
		} else {
			this._budgetReport.setValue({ value, column, row });
		}

		this._invalidateValuesOfAutomatedCells();

		this._onBudgetChanged(() => {
			this._updateValuesOfAutomatedCells();
		});
	}

	_invalidateValuesOfAutomatedCells() {
		this._budgetEditorFieldWidgetCollector.forEach((widget) =>
			widget.setTemporaryValue(),
		);
	}

	_updateValuesOfAutomatedCells({ force = false } = {}) {
		this._budgetEditorFieldWidgetCollector.forEach((widget) => {
			const isYTDColumn = this._isYearToDateColumn(widget.getColumn());
			widget.updateValue({ force: force || isYTDColumn });
		});
	}

	_onBudgetFieldKeyup({ event, column, row, budgetField }) {
		const Escape = 27;
		const Enter = 13;
		const Left = 37;
		const Up = 38;
		const Right = 39;
		const Down = 40;

		if (event.keyCode === Escape) {
			budgetField.update();
			budgetField.select();
			return;
		}

		switch (event.keyCode) {
			case Left: {
				this._goLeftFrom(column, row);
				break;
			}
			case Up: {
				this._goUpFrom(column, row);
				break;
			}
			case Enter:
			case Right: {
				this._goRightFrom(column, row);
				break;
			}
			case Down: {
				this._goDownFrom(column, row);
				break;
			}
			default: {
				return;
			}
		}
	}

	_goLeftFrom(column, row) {
		this._focusInput({
			row,
			column: this.getReport().findPreviousVisibleEditableColumnFrom(
				column,
			),
		});
	}

	_goUpFrom(column, row) {
		this._focusInput({
			row: this.getReport().findPreviousVisibleAccountRowFrom(row),
			column,
		});
	}

	_goRightFrom(column, row) {
		this._focusInput({
			column: this.getReport().findNextVisibleEditableColumnFrom(column),
			row,
		});
	}

	_goDownFrom(column, row) {
		this._focusInput({
			column,
			row: this.getReport().findNextVisibleAccountRowFrom(row),
		});
	}

	_focusInput({ column, row }) {
		const inputId = BudgetEditorFieldWidget.generateInputId({
			column,
			row,
		});
		const input = jQuery(`#${inputId}`);

		if (input.length) {
			input[0].focus();
			input[0].select();
		}
	}

	_budgetEditorFieldRenderer(row, column) {
		const fieldWidget = new BudgetEditorFieldWidget({
			budgetReport: this._budgetReport,
			column,
			row,
			onBudgetFieldChanged: this._onBudgetFieldChanged.bind(this),
			onBudgetFieldKeyup: this._onBudgetFieldKeyup.bind(this),
		});

		this._budgetEditorFieldWidgetCollector.push(fieldWidget);

		return fieldWidget;
	}
}
